import AnalyticsHandler from './handler.js'
import { path } from 'ramda'
import { DEFAULT_PAYLOAD, HANDLER_METHODS } from '@/lib/analytics/consts.ts'

import { getBaseProperties } from '@/lib/analytics/helpers.js'

import { useLogger } from '@booksy-com/vue-tools'
import { useToast } from '@/composables/toast'
import { getFlag } from '@/lib/flags'

const logger = useLogger('google-tag-manager-handler')
const MAX_EVENT_PARAMETERS = 25

export default class GoogleTagManager extends AnalyticsHandler {
  __initialize () {
    // used to store information about all sent events
    this.handlerStack = null
    // GTM handler instance
    const handler = window[this.config.name]

    // shortcut: if handler is ok
    if (handler && typeof handler.push === 'function') {
      // done
      this.handlerStack = []
      return super.__initialize()
    }

    this.initializationError(`window.${this.config.name} not set`)
    return true // do not retry...
  }

  send (event) {
    const handler = window[this.config.name]
    let eventData = {
      ...event,
    }

    eventData._clear = true
    eventData = this.__applyEventMiddlewares(eventData)

    this.validate(eventData)
    this.sendToExternalToolIfAllowed(
      () => handler.push(eventData),
      eventData,
      this.handlerStack,
      HANDLER_METHODS.PUSH
    )

    this.handlerStack.push(eventData)
  }

  __applyEventMiddlewares (event) {
    const eventMiddlewares = this.config.eventMiddlewares || {}
    const middleware = eventMiddlewares[event?.event]

    if (typeof middleware === 'function') {
      try {
        return middleware(event)
      } catch (e) {
        this.logger.error('Event middleware failed', { event: event?.event, error: e })
        return event
      }
    }

    return event
  }

  eventPageViewHandler ({ data }) {
    const to = path(['to'], data) || {}

    return {
      event: 'Page_View',
      properties: {
        page_type: to.name,
        page_path: to.fullPath,
      },
    }
  }

  [DEFAULT_PAYLOAD] (data) {
    const baseProperties = getBaseProperties(data)
    const { ...properties } = baseProperties

    return {
      properties,
    }
  }

  validate (event) {
    const eventErrors = []
    // Limits for GA4;
    // for more info check:
    // https://support.google.com/analytics/answer/9267744?hl=en
    const MAX_EVENT_NAME_LENGTH = 40
    const MAX_EVENT_PARAMETER_NAME_LENGTH = 40
    const MAX_EVENT_PARAMETER_VALUE_LENGTH = {
      'any': 100,
      'page_title': 300,
      'page_referrer': 420,
      'page_location': 1000,
    }

    if (event.event?.length > MAX_EVENT_NAME_LENGTH) {
      eventErrors.push(`Event validation: ${event.event} - too long name (max: ${MAX_EVENT_NAME_LENGTH})`)
    }

    const propertiesCount = Object.keys(event?.properties || {})?.length || 0
    if (propertiesCount > MAX_EVENT_PARAMETERS) {
      eventErrors.push(`Event validation: ${event.event} - too many properties (${propertiesCount}; limit is ${MAX_EVENT_PARAMETERS})`)
    }

    Object.keys(event?.properties || {})?.forEach(key => {
      if (key?.length > MAX_EVENT_PARAMETER_NAME_LENGTH) {
        eventErrors.push(`Event validation: ${event.event}.${key} - too long parameter name (${key?.length}; limit is ${MAX_EVENT_PARAMETER_NAME_LENGTH})`)
      }

      const propertyValueLimit = MAX_EVENT_PARAMETER_VALUE_LENGTH[key]
        || MAX_EVENT_PARAMETER_VALUE_LENGTH['any']
      const valueLength = event?.properties?.[key]?.length
      if (valueLength > propertyValueLimit) {
        eventErrors.push(`Event validation: ${event.event}.${key} - too long value (${valueLength}; limit is ${propertyValueLimit})`)
      }
    })

    if (eventErrors?.length > 0) {
      logger.warn(eventErrors.join('\n'))

      if (!this.isProduction()) {
        const toast = useToast()
        const shouldShowAnalyticsWarning = getFlag('showAnalyticsWarningToast', false)
        if (shouldShowAnalyticsWarning && !window.navigator.webdriver) {
          toast.warn(eventErrors.join('<br/>'), {
            duration: 5000,
          })
        }
      }
    }

    return eventErrors
  }
}
